import React, { useEffect, useState } from 'react';

import { Web } from '@mui/icons-material';
import { Form } from 'react-bootstrap';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { MatchCalcForm } from './Form';
import MatchCalc401KTable, { matchCalcColumns } from './MatchCalc401KTable';
import Excel from '../../../assets/excel.png';
import { Breadcrumbs, Button, EdgeReportingWindow, SubHeader, ReportLoader } from '../../../components';
import { newDateFormatted } from '../../../utils';
import ImageColorChanger from '../../../utils/imageColorChanger';
import getCSVData from '../csv';
import getEdgeReportingMenu from '../getEdgeReportingMenu';
import { userHasAccess, generateReport, getReportData, removeReportData } from '../operations';

import './styles.scoped.css';

const MatchCalcWindow = ({ form, setForm, handleChange, handleViewResults, errors, loadingResults }) => {
  const navigate = useNavigate();
  return (
    <Form
      noValidate
      onSubmit={e => e.preventDefault()}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <MatchCalcForm form={form} setForm={setForm} handleChange={handleChange} errors={errors} />
      <div className="buttons-row">
        {/* Per SOP, keep the action enabled and validate on press (inline field errors);
            reserve disabled for the in-flight state only. */}
        <Button
          variant="primary"
          disabled={loadingResults}
          onPress={() => handleViewResults()}
          style={{ marginRight: '20px', width: '100px' }}
          text="View"
        />
        <Button
          style={{ width: '100px' }}
          onPress={() => navigate('/edge-reporting')}
          variant="outline"
          text="Cancel"
        />
      </div>
    </Form>
  );
};

const MatchCalc401K = () => {
  const { t } = useTranslation();
  const { menuItems } = getEdgeReportingMenu(t);
  const { user } = useSelector(state => state.admin);
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [form, setForm] = useState({ EffectiveDate: '', MatchPercentage: '0', Threshold: '24500' });
  const [loadingResults, setLoadingResults] = useState(false);
  const [showResults, setShowResults] = useState(false);
  // Per-field validation errors, surfaced inline on View (SOP: no disabled gating, no alert).
  const [errors, setErrors] = useState({});

  const handleChange = e => {
    const { name, value } = e.target;
    // Clear a field's validation error as soon as the user edits it.
    if (value && errors?.[name]) setErrors(prev => ({ ...prev, [name]: undefined }));
    setForm(prev => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (menuItems) {
      // eslint-disable-next-line no-undef
      if (!userHasAccess(user, menuItems.find(i => i.to === window.location.pathname)?.id)) navigate('/');
    }
  }, [menuItems]);

  const fileName = `edge-reporting-401k-match-calculation-${newDateFormatted.toString()}.csv`;

  const handleViewResults = async () => {
    // Validate on press and surface errors inline beneath each field (SOP — no disabled gating, no alert).
    const nextErrors = {};
    if (!form?.EffectiveDate) nextErrors.EffectiveDate = 'Effective Date is required.';
    if (form?.MatchPercentage === '' || Number.isNaN(Number(form?.MatchPercentage)))
      nextErrors.MatchPercentage = '401K Match Percentage is required.';
    if (form?.Threshold === '' || Number.isNaN(Number(form?.Threshold)))
      nextErrors.Threshold = 'Total Contribution Threshold Value is required.';
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    setErrors({});
    setLoadingResults(true);
    try {
      const response = await generateReport({
        // reportName '401k-mc-report' maps to proc
        // sp_cl_cp_bi_401kmc(@EffectiveDate, @MatchPercentage, @TotalContributionThreshold) in the
        // cp-appsync-long-running-report resolver. @MatchPercentage is a decimal fraction — the
        // entered percentage (e.g. 25) is divided by 100 (0.25) before it is sent.
        endpoint: '401k-mc-report',
        payload: {
          EffectiveDate: form?.EffectiveDate,
          MatchPercentage: Number(form?.MatchPercentage) / 100,
          TotalContributionThreshold: Number(form?.Threshold),
        },
      });
      if (response?.location) {
        const reportData = await getReportData(response.location);
        if (reportData?.length) {
          await removeReportData(response.location);
          setData(reportData);
          setShowResults(true);
        } else {
          // eslint-disable-next-line no-undef
          alert('No data found matching your criteria.');
        }
      } else {
        // eslint-disable-next-line no-undef
        alert(response?.statusDescription || 'No data found matching your criteria.');
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingResults(false);
    }
  };

  return (
    <>
      <SubHeader
        text="EDGE Reporting"
        buttons={[{ icon: <Web sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigate('/dashboard') }]}
        popoverTitle="Need help with your 401K Match Calculation Report?"
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px',
        }}>
        <Breadcrumbs
          leftTitle="Reporting Home"
          middleTitle={showResults && '401K Match Calculation'}
          midItemAction={() => setShowResults(false)}
          rightTitle={showResults ? 'Viewing Report' : '401K Match Calculation'}
          route="/edge-reporting"
        />
        {!!data?.length && showResults && (
          <CSVLink filename={fileName} data={getCSVData(data, matchCalcColumns)}>
            <ImageColorChanger
              src={Excel}
              style={{ padding: '20px 30px 0 0 ', cursor: 'pointer' }}
              alt="export-to-excel"
            />
          </CSVLink>
        )}
      </div>
      {loadingResults && <ReportLoader didAbort={() => setLoadingResults(false)} />}
      {showResults ? (
        <>
          <MatchCalc401KTable data={data} loading={loadingResults} />
          <div className="buttons-row">
            <Button
              variant="primary"
              onPress={() => setShowResults(false)}
              style={{ marginRight: '20px', width: '100px' }}
              text="Back"
            />
            <Button
              style={{ width: '100px' }}
              onPress={() => navigate('/edge-reporting')}
              variant="outline"
              text="Cancel"
            />
          </div>
        </>
      ) : (
        <EdgeReportingWindow
          containerStyle={{
            border: '2px solid #343a40',
            minHeight: 200,
          }}
          windowContent={
            <MatchCalcWindow
              form={form}
              setForm={setForm}
              handleChange={handleChange}
              handleViewResults={handleViewResults}
              errors={errors}
              loadingResults={loadingResults}
            />
          }
          windowTitle="Criteria"
          headerStyle={{ backgroundColor: '#343a40' }}
        />
      )}
    </>
  );
};

export default MatchCalc401K;
